import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { type ABCClassification } from '@/components/common/abc';

/**
 * ABCBadge Component
 *
 * Displays ABC classification for inventory items with appropriate color coding:
 * - A items (Critical): Red/Rose - High value, tight control
 * - B items (Important): Blue - Moderate value, standard control
 * - C items (Low Priority): Gray/Slate - Low value, simple control
 */

interface ABCBadgeProps {
  classification: ABCClassification;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const classificationConfig = {
  A: {
    label: 'Class A',
    shortLabel: 'A',
    description: 'Critical - High Value',
    className:
      'bg-status-destructive-subtle text-status-destructive-foreground border-status-destructive-border hover:bg-status-destructive-subtle',
  },
  B: {
    label: 'Class B',
    shortLabel: 'B',
    description: 'Important - Moderate Value',
    className:
      'bg-status-info-subtle text-status-info-foreground border-status-info-border hover:bg-status-info-subtle',
  },
  C: {
    label: 'Class C',
    shortLabel: 'C',
    description: 'Low Priority - Low Value',
    className: 'bg-muted text-muted-foreground border-border hover:bg-muted',
  },
} as const;

export function ABCBadge({
  classification,
  size = 'md',
  showLabel = false,
  className,
}: ABCBadgeProps) {
  // Handle null/undefined classification
  if (!classification) {
    return (
      <Badge
        variant="outline"
        className={cn(
          'bg-muted text-muted-foreground border-border',
          size === 'sm' && 'text-xs px-2 py-0.5',
          size === 'md' && 'text-sm px-2.5 py-1',
          size === 'lg' && 'text-base px-3 py-1.5',
          className
        )}
      >
        {showLabel ? 'Not Classified' : '—'}
      </Badge>
    );
  }

  const config = classificationConfig[classification];

  return (
    <Badge
      variant="outline"
      className={cn(
        'font-semibold transition-colors',
        config.className,
        size === 'sm' && 'text-xs px-2 py-0.5',
        size === 'md' && 'text-sm px-2.5 py-1',
        size === 'lg' && 'text-base px-3 py-1.5',
        className
      )}
      title={config.description}
    >
      {showLabel ? config.label : config.shortLabel}
    </Badge>
  );
}

/**
 * ABCBadgeWithLegend Component
 *
 * Shows an ABC badge along with a legend explaining the classification system
 */
interface ABCBadgeWithLegendProps {
  classification: ABCClassification;
  showFullLegend?: boolean;
  className?: string;
}

export function ABCBadgeWithLegend({
  classification,
  showFullLegend = false,
  className,
}: ABCBadgeWithLegendProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <ABCBadge classification={classification} />
      {showFullLegend && classification && (
        <span className="text-xs text-muted-foreground">
          {classificationConfig[classification].description}
        </span>
      )}
    </div>
  );
}

/**
 * ABCLegend Component
 *
 * Displays a legend explaining the ABC classification system
 */
export function ABCLegend({ className }: { className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-4', className)}>
      <span className="text-sm font-medium text-muted-foreground">ABC Classification:</span>
      <div className="flex items-center gap-2">
        <ABCBadge classification="A" />
        <span className="text-xs text-muted-foreground">~80% value</span>
      </div>
      <div className="flex items-center gap-2">
        <ABCBadge classification="B" />
        <span className="text-xs text-muted-foreground">~15% value</span>
      </div>
      <div className="flex items-center gap-2">
        <ABCBadge classification="C" />
        <span className="text-xs text-muted-foreground">~5% value</span>
      </div>
    </div>
  );
}
